import { tokens } from "@/tokens";
import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  Alert,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocalShipping as ShippingIcon,
  Factory as FactoryIcon,
  Store as StoreIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { TextInput } from '@/components';
import { ValidationIssue, getRequiredFieldsForSection } from './validation-utils';
import ItemsEditTable from './items-edit-table';

// Type guard to check if details are CertifierDetails
const isCertifierDetails = (details: CertifierDetails | ExporterDetails | ProducerDetails | ImporterDetails): details is CertifierDetails => {
  return 'yourName' in details || 'title' in details;
};

interface ExpandedCertificateFormProps {
  section: 'certifier' | 'exporter' | 'producer' | 'importer' | 'items';
  data: CertificateExtractedData;
  onUpdate: (updatedData: CertificateExtractedData) => void;
  formType: string;
  validationIssues: ValidationIssue[];
}

const ExpandedCertificateForm: React.FC<ExpandedCertificateFormProps> = ({
  section,
  data,
  onUpdate,
  formType,
  validationIssues,
}) => {
  const handleFieldChange = (field: string, value: string) => {
    const detailsKey = `${section}Details` as keyof CertificateExtractedData;
    
    if (section === 'certifier') {
      // Handle certifier separately since it has both type and details
      onUpdate({
        ...data,
        certifierDetails: {
          ...data.certifierDetails,
          [field]: value,
        } as CertifierDetails,
      });
    } else {
      onUpdate({
        ...data,
        [detailsKey]: {
          ...(data[detailsKey] as any),
          [field]: value,
        },
      });
    }
  };

  const handleItemsUpdate = (items: CertificateItem[]) => {
    onUpdate({
      ...data,
      items,
    });
  };

  const getFieldError = (field: string) => {
    const fullField = section === 'certifier' && field !== 'certifier' 
      ? `certifierDetails.${field}` 
      : section === 'certifier' 
      ? `certifierDetails.${field}`
      : `${section}Details.${field}`;
    return validationIssues.find(issue => issue.field === fullField && issue.type === 'error');
  };

  const getFieldWarning = (field: string) => {
    const fullField = section === 'certifier' && field !== 'certifier' 
      ? `certifierDetails.${field}` 
      : section === 'certifier' 
      ? `certifierDetails.${field}`
      : `${section}Details.${field}`;
    return validationIssues.find(issue => issue.field === fullField && issue.type === 'warning');
  };

  const getSectionIcon = () => {
    switch (section) {
      case 'certifier':
        return <BusinessIcon sx={{ color: tokens.brand.primary }} />;
      case 'exporter':
        return <ShippingIcon sx={{ color: tokens.brand.primary }} />;
      case 'producer':
        return <FactoryIcon sx={{ color: tokens.brand.primary }} />;
      case 'importer':
        return <StoreIcon sx={{ color: tokens.brand.primary }} />;
      case 'items':
        return <InventoryIcon sx={{ color: tokens.brand.primary }} />;
    }
  };

  const getSectionTitle = () => {
    switch (section) {
      case 'certifier':
        return 'Certifier Information';
      case 'exporter':
        return 'Exporter Details';
      case 'producer':
        return 'Producer Details';
      case 'importer':
        return 'Importer Details';
      case 'items':
        return 'Product Items';
    }
  };

  const requiredFields = getRequiredFieldsForSection(section);

  if (section === 'items') {
    return (
      <Box>
        <ItemsEditTable
          items={data.items || []}
          onUpdate={handleItemsUpdate}
          formType={formType}
          validationIssues={validationIssues}
        />
      </Box>
    );
  }

  const detailsKey = section === 'certifier' ? 'certifierDetails' : `${section}Details` as keyof CertificateExtractedData;
  const details = data[detailsKey] as CertifierDetails | ExporterDetails | ProducerDetails | ImporterDetails | undefined;

  return (
    <Box>
      <Paper sx={{ 
        p: 2, 
        borderRadius: tokens.radius.md,
        border: `1px solid ${tokens.border.default}`,
        backgroundColor: '#fafbfc',
      }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          {getSectionIcon()}
          <Typography variant="subtitle1" fontWeight={tokens.fontWeight.bold} color={tokens.text.body}>
            {getSectionTitle()}
          </Typography>
        </Stack>

        {section === 'certifier' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter the details of the {data.certifier || 'selected certifier'} here. This information will be used as the certifier on the certificate.
          </Alert>
        )}

        {section === 'exporter' && data.certifier === 'Exporter' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Leave this section blank</strong> - The Exporter is already the certifier. Their details should be entered in the "Certifier Details" section above.
          </Alert>
        )}

        {section === 'importer' && data.certifier === 'Importer' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Leave this section blank</strong> - The Importer is already the certifier. Their details should be entered in the "Certifier Details" section above.
          </Alert>
        )}

        {section === 'producer' && data.certifier === 'Producer' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Leave this section blank</strong> - The Producer is already the certifier. Their details should be entered in the "Certifier Details" section above.
          </Alert>
        )}

        {section === 'producer' && data.certifier !== 'Producer' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Producer information is optional. Leave blank if the producer is the same as the exporter.
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Company Name - First field for all sections */}
          <Grid item xs={12}>
            <TextInput
              label="Company Name"
              type="text"
              value={details?.companyName || ''}
              handleChangeValue={(val) => handleFieldChange('companyName', val)}
              required={requiredFields.includes('companyName')}
              placeholder="e.g., ABC Manufacturing Inc."
              error={!!getFieldError('companyName')}
              helperText={getFieldError('companyName')?.message}
            />
          </Grid>
          
          {section === 'certifier' && details && isCertifierDetails(details) && (
            <>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Your Name"
                  type="text"
                  value={details.yourName || ''}
                  handleChangeValue={(val) => handleFieldChange('yourName', val)}
                  required={requiredFields.includes('yourName')}
                  placeholder="e.g., John Doe"
                  error={!!getFieldError('yourName')}
                  helperText={getFieldError('yourName')?.message || getFieldWarning('yourName')?.message}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Title"
                  type="text"
                  value={details.title || ''}
                  handleChangeValue={(val) => handleFieldChange('title', val)}
                  required={requiredFields.includes('title')}
                  placeholder="e.g., Export Manager"
                  error={!!getFieldError('title')}
                  helperText={getFieldError('title')?.message}
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12}>
            <TextInput
              label="Address"
              type="text"
              value={details?.address || ''}
              handleChangeValue={(val) => handleFieldChange('address', val)}
              required={requiredFields.includes('address')}
              placeholder="e.g., 123 Main Street, Suite 100"
              error={!!getFieldError('address')}
              helperText={getFieldError('address')?.message}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              label="Country & Postal Code"
              type="text"
              value={details?.countryAndPostalCode || ''}
              handleChangeValue={(val) => handleFieldChange('countryAndPostalCode', val)}
              required={requiredFields.includes('countryAndPostalCode')}
              placeholder="e.g., USA 12345"
              error={!!getFieldError('countryAndPostalCode')}
              helperText={getFieldError('countryAndPostalCode')?.message}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              label="Phone"
              type="tel"
              value={details?.phone || ''}
              handleChangeValue={(val) => handleFieldChange('phone', val)}
              placeholder="e.g., +1 555-123-4567"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              label="Email"
              type="email"
              value={details?.email || ''}
              handleChangeValue={(val) => handleFieldChange('email', val)}
              placeholder="e.g., contact@company.com"
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ExpandedCertificateForm;